/**
 * Shared utilities for reconnecting to a Valkey SSE stream.
 *
 * Used both by useMessageSender (mid-session drop recovery) and
 * useStreamingPoller (page-refresh / thread-switch recovery).
 */

import { apiAuthType, apiBaseUrl } from '@/config/env';
import type { LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';

export async function reconnectToStream(
  projectId: string,
  threadId: string,
  messageId: string,
  token: string,
  controller: AbortController,
  streamProcessor: LLMEventStreamProcessor,
  lastEventId: string = '0-0',
): Promise<void> {
  const url =
    `${apiBaseUrl}/reasoner/projects/${projectId}` +
    `/threads/${threadId}` +
    `/messages/${messageId}` +
    `/stream?lastEventId=${encodeURIComponent(lastEventId)}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Auth-Type': apiAuthType,
      'X-Connection-Type': 'stream',
    },
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Reconnect failed: ${response.status}`);
  }

  await streamProcessor.processStream(
    response,
    threadId,
    null,
    undefined,
    undefined,
    undefined,
  );
}

/**
 * Set the message ID override on the stream processor, call
 * reconnectToStream, then clear the override regardless of outcome.
 *
 * Encapsulates the set-override → reconnect → clear-override pattern
 * shared by useMessageSender and useStreamingPoller.
 */
export async function reconnectWithState(
  projectId: string,
  threadId: string,
  messageId: string,
  token: string,
  controller: AbortController,
  streamProcessor: LLMEventStreamProcessor,
  lastEventId: string = '0-0',
): Promise<void> {
  streamProcessor.setMessageIdOverride(messageId, false, threadId);
  try {
    await reconnectToStream(
      projectId,
      threadId,
      messageId,
      token,
      controller,
      streamProcessor,
      lastEventId,
    );
  } finally {
    streamProcessor.setMessageIdOverride(null, false, threadId);
  }
}
